import { useTranslation } from "react-i18next"

import type {
  FairUseEntitlementCellError,
  FairUseEntitlementFieldMeta,
  FairUseEntitlementFormRow,
  FairUseEntitlementsFormState,
} from "./fair-use-entitlements-utils"
import type {
  FairUseEntitlementSchema,
  FairUseReasoningEffort,
  FairUseTier,
} from "@/api/admin/fair-use"

import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"

import {
  fairUseEntitlementCellId,
  fairUseEntitlementFields,
  fairUseEntitlementPlans,
} from "./fair-use-entitlements-utils"

type FairUseEntitlementsMatrixProps = {
  form: FairUseEntitlementsFormState
  schema?: FairUseEntitlementSchema
  /** Mirrors `PLATFORM_ONLY_CONFIG_FIELDS`: tenants read, the platform writes. */
  canEdit: boolean
  /** Cells that do not parse, so the exact box is flagged rather than the panel. */
  issues: FairUseEntitlementCellError[]
  onChange: (plan: FairUseTier, patch: Partial<FairUseEntitlementFormRow>) => void
}

/** The locale key holding each plan's display name, so one map drives both uses. */
const PLAN_LABEL_KEYS: Record<FairUseTier, { key: string; fallback: string }> = {
  standard: { key: "adminDashboard.fairUse.plans.standard", fallback: "Standard" },
  pro: { key: "adminDashboard.fairUse.plans.pro", fallback: "Pro" },
  unlimited: { key: "adminDashboard.fairUse.plans.unlimited", fallback: "Unlimited" },
}

/**
 * One matrix cell — the editor the field's TYPE calls for.
 *
 * Every cell is labelled for assistive technology with "<field>, <plan>": the
 * visible label sits in the row header, and a screen reader landing on box 27
 * of 36 otherwise hears only "Pro".
 */
function EntitlementCell({
  meta,
  plan,
  row,
  canEdit,
  hasIssue,
  ariaLabel,
  onChange,
}: {
  meta: FairUseEntitlementFieldMeta
  plan: FairUseTier
  row: FairUseEntitlementFormRow
  canEdit: boolean
  hasIssue: boolean
  ariaLabel: string
  onChange: (patch: Partial<FairUseEntitlementFormRow>) => void
}) {
  const { t } = useTranslation()
  const id = fairUseEntitlementCellId(plan, meta.name)

  if (meta.type === "effort") {
    if (!canEdit) {
      return (
        <span className="text-sm" data-testid={id}>
          {t(`adminDashboard.fairUse.settings.fallback.effort.${row.max_reasoning_effort}`, {
            defaultValue: row.max_reasoning_effort,
          })}
        </span>
      )
    }
    return (
      <Select
        value={row.max_reasoning_effort}
        onValueChange={(value) =>
          onChange({ max_reasoning_effort: value as FairUseReasoningEffort })
        }
      >
        <SelectTrigger aria-label={ariaLabel} className="h-9" id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {meta.options.map((option) => (
            <SelectItem key={option} value={option}>
              {t(`adminDashboard.fairUse.settings.fallback.effort.${option}`, {
                defaultValue: option,
              })}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  if (meta.type === "boolean") {
    const field = meta.name === "voice" ? "voice" : "deep"
    const checked = row[field]
    if (!canEdit) {
      return (
        <Badge data-testid={id} variant={checked ? "secondary" : "outline"}>
          {checked
            ? t("adminDashboard.fairUse.settings.entitlements.included", "Included")
            : t("adminDashboard.fairUse.settings.entitlements.notIncluded", "Not included")}
        </Badge>
      )
    }
    return (
      <Switch
        aria-label={ariaLabel}
        checked={checked}
        id={id}
        onCheckedChange={(next) =>
          onChange({ [field]: next } as Partial<FairUseEntitlementFormRow>)
        }
      />
    )
  }

  const value = row[meta.name as keyof FairUseEntitlementFormRow] as string
  if (!canEdit) {
    return (
      <span className="text-sm tabular-nums" data-testid={id}>
        {value.trim() === ""
          ? t("adminDashboard.fairUse.settings.entitlements.noLimit", "No limit")
          : value}
      </span>
    )
  }
  return (
    <Input
      aria-invalid={hasIssue}
      aria-label={ariaLabel}
      className={`h-9 w-28 tabular-nums ${hasIssue ? "border-destructive" : ""}`}
      id={id}
      // The SEK budget is the one cell where "5,89" is the value rather than a
      // typo, so it gets the decimal keypad and the decimal parser.
      inputMode={meta.type === "number" ? "decimal" : "numeric"}
      type="text"
      value={value}
      placeholder={
        meta.nullable
          ? t("adminDashboard.fairUse.settings.entitlements.noLimit", "No limit")
          : t("adminDashboard.fairUse.settings.entitlements.requiredPlaceholder", "Required")
      }
      onChange={(event) =>
        onChange({ [meta.name]: event.target.value } as Partial<FairUseEntitlementFormRow>)
      }
    />
  )
}

/**
 * What each plan may DO, next to what it may spend (#3087, spec R1).
 *
 * Rows are entitlements and columns are plans on purpose: an admin reads this
 * surface to answer "what does Pro get that Standard does not", which is a
 * comparison ALONG a row. The transpose — a card per plan — reads well for one
 * plan at a time and makes that comparison a scroll.
 *
 * Nothing here is delegable. The matrix decides whether a tenant's users get
 * Deep, voice, scheduled flows and how large a Deep turn may grow, so a
 * delegated admin sending it answers 403 `platform_admin_required` and applies
 * NOTHING — not even the thresholds in the same payload. Tenant admins
 * therefore get the same matrix rendered as values, not as disabled boxes:
 * a padlocked input invites an edit that cannot happen and says nothing about
 * who can make it.
 *
 * A blank cell is "no limit", and only the four fields the platform marks
 * `nullable` accept it. The rest are real per-turn budgets: an unbounded Deep
 * token budget is not a plan the platform sells, so their blank is an error
 * flagged on the box itself rather than a generous default.
 */
export function FairUseEntitlementsMatrix({
  form,
  schema,
  canEdit,
  issues,
  onChange,
}: FairUseEntitlementsMatrixProps) {
  const { t } = useTranslation()
  const fields = fairUseEntitlementFields(schema)
  const plans = fairUseEntitlementPlans(schema)
  const issueKeys = new Set(issues.map((issue) => `${issue.plan}:${issue.field}`))
  const planName = (plan: FairUseTier) =>
    t(PLAN_LABEL_KEYS[plan].key, PLAN_LABEL_KEYS[plan].fallback)
  const fieldName = (name: string) =>
    t(`adminDashboard.fairUse.settings.entitlements.fields.${name}`, { defaultValue: name })

  return (
    <div className="space-y-3" data-testid="fair-use-entitlements-matrix">
      <div className="space-y-1">
        <Label className="text-sm font-semibold">
          {t("adminDashboard.fairUse.settings.entitlements.title", "What each plan can do")}
        </Label>
        <p className="text-muted-foreground max-w-prose text-xs">
          {canEdit
            ? t(
                "adminDashboard.fairUse.settings.entitlements.caption",
                "Applies only while fair use is on. Leave a box empty for no limit, where the platform allows it."
              )
            : t(
                "adminDashboard.fairUse.settings.entitlements.readOnlyCaption",
                "Set by your platform provider, and applies only while fair use is on."
              )}
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[34rem] border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th className="text-muted-foreground border-b py-2 pr-4 text-left text-xs font-semibold tracking-wider uppercase">
                {t("adminDashboard.fairUse.settings.entitlements.featureColumn", "Entitlement")}
              </th>
              {plans.map((plan) => (
                <th
                  key={plan}
                  className="text-muted-foreground border-b px-3 py-2 text-left text-xs font-semibold tracking-wider uppercase"
                >
                  {planName(plan)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fields.map((meta) => (
              <tr key={meta.name}>
                <th className="border-b py-2 pr-4 text-left align-middle font-normal">
                  <span className="block">{fieldName(meta.name)}</span>
                  <span className="text-muted-foreground text-xs">
                    {t(`adminDashboard.fairUse.settings.entitlements.help.${meta.name}`, {
                      defaultValue: "",
                    })}
                  </span>
                </th>
                {plans.map((plan) => (
                  <td key={plan} className="border-b px-3 py-2 align-middle">
                    <EntitlementCell
                      ariaLabel={`${fieldName(meta.name)}, ${planName(plan)}`}
                      canEdit={canEdit}
                      hasIssue={issueKeys.has(`${plan}:${meta.name}`)}
                      meta={meta}
                      plan={plan}
                      row={form[plan]}
                      onChange={(patch) => onChange(plan, patch)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canEdit && issues.length > 0 ? (
        <p className="text-destructive text-xs" data-testid="fair-use-entitlements-error">
          {t(
            "adminDashboard.fairUse.settings.entitlements.rangeError",
            "The highlighted boxes need a number inside the platform's range — whole, except the SEK budget — or, where no limit is allowed, nothing at all."
          )}
        </p>
      ) : null}
    </div>
  )
}
